import { isNonNullable } from '../../helpers'
import {
	ConfigError,
	FileError,
	FormattedZodError,
	GuideErrorMap,
	GuideFileErrorMap,
	ProjectErrors,
} from '../../types'

export function checkForErrors(rawErrors: ProjectErrors['rawErrors']): boolean {
	return (
		rawErrors.duplicatedSlugs.length > 0 ||
		hasItemsAtLowestLevel(rawErrors.duplicatedPaths) ||
		hasItemsAtLowestLevel(rawErrors.missingFiles) ||
		hasItemsAtLowestLevel(rawErrors.contentErrors) ||
		hasItemsAtLowestLevel(rawErrors.configurationErrors)
	)
}

export type ErrorPrinterCallback<T> = (filePath: string, errors: T[]) => string

export function processGuideErrorMap<T>(
	errorMap: GuideErrorMap<T>,
	callback: ErrorPrinterCallback<T>,
): string {
	let output = ''
	for (const [filePath, errors] of errorMap) {
		output += callback(filePath, errors)
	}
	return output
}

export function processGuideFileErrorMap<T>(
	errorMap: GuideFileErrorMap<T>,
	callback: ErrorPrinterCallback<T>,
): string {
	let output = ''

	for (const [guidePath, versionErrorMap] of errorMap) {
		if (hasItemsAtLowestLevel(versionErrorMap)) {
			output += `Guide: ${guidePath.split('/').pop()}\n`
			for (const [versionPath, fileErrorMap] of versionErrorMap) {
				output += `  Version: ${versionPath.split('/').pop()}\n`
				for (const [filePath, errors] of fileErrorMap) {
					output += `   - ${callback(filePath, errors)}`
				}
			}
		}
	}
	return output
}

type ConfigErrorPrinterCallback = (
	filePath: string,
	errors: FileError<ConfigError> | FormattedZodError[],
) => string

export function printConfigurationErrors(
	configurationErrors: Map<string, FileError<ConfigError> | FormattedZodError[]>,
): string {
	let output = 'Configuration Errors:\n'
	for (const [filePath, fileError] of configurationErrors) {
		output += configErrorPrinter(filePath, fileError)
	}
	return output
}

// Example usage:
const configErrorPrinter: ConfigErrorPrinterCallback = (filePath, fileError) => {
	let errorOutput = `\nFile: ${filePath}\n`

	if (Array.isArray(fileError)) {
		// Handle FormattedZodError[]
		fileError.forEach((zodError) => {
			errorOutput += `  - ${zodError.path}: ${zodError.message}\n`
		})
	} else {
		// Handle FileError<ConfigError>
		fileError.references.forEach((error) => {
			errorOutput += `  - ${error.message}${error.resolution ? `: ${error.resolution}` : ''}\n`
			if (error.additionalInformation) {
				error.additionalInformation.forEach((information) => {
					errorOutput += `    - ${information}\n`
				})
			}
		})
	}

	return errorOutput
}

export const hasItemsAtLowestLevel = (map: Map<unknown, unknown>): boolean => {
	if (map.size === 0) {
		return false
	}
	for (const value of map.values()) {
		if (value instanceof Map) {
			if (hasItemsAtLowestLevel(value)) {
				return true
			}
		} else if (Array.isArray(value)) {
			if (value.length > 0) {
				return true
			}
		} else if (typeof value === 'object') {
			if (value) {
				return true
			}
		}
	}
	return false
}

export function processErrors({
	configurationErrors,
	contentErrors,
	duplicatedPaths,
	duplicatedSlugs,
	missingFiles,
}: ProjectErrors['rawErrors']) {
	return {
		duplicatedSlugs: {
			errorOutput: duplicatedSlugs
				.map((error) => {
					let output = `Duplicated Slug: ${error.slug}\n`
					output += ` - Guide: ${'configFilePath' in error.guide ? error.guide.configFilePath : error.guide.url}\n`
					output += ` - Other Guide: ${'configFilePath' in error.otherGuide ? error.otherGuide.configFilePath : error.otherGuide.url}\n`
					return output
				})
				.join('\n'),
			hasErrors: duplicatedSlugs.length > 0,
		},
		duplicatedPaths: {
			errorOutput: processGuideErrorMap(duplicatedPaths, (_, errors) => {
				let output = ''
				for (const error of errors) {
					output += ` - Duplicated Path: ${error.path}\n`
					output += `   Item: ${'filePath' in error.item ? error.item.filePath : error.item.url}\n`
					output += `   Other Item: ${'filePath' in error.otherItem ? error.otherItem.filePath : error.otherItem.url}\n`
				}
				return output
			}),
			hasErrors: hasItemsAtLowestLevel(duplicatedPaths),
		},
		contentErrors: {
			errorOutput: processGuideFileErrorMap(contentErrors, (filePath, errors) => {
				let output = `File: ${filePath}\n`
				for (const error of errors) {
					output += `     Content Error: ${error.message}\n`
					for (const zodError of error.zodErrors ?? []) {
						output += `      [${zodError.path}]: ${zodError.message}\n`
					}
				}
				return output
			}),
			hasErrors: hasItemsAtLowestLevel(contentErrors),
		},
		missingFiles: {
			errorOutput: processGuideFileErrorMap(missingFiles, (filePath, errors) => {
				let output = `File: ${filePath}\n`
				for (const error of errors) {
					if (error.missingFilePath.length > 0)
						output += `	- Missing: ${error.missingFilePath} ${isNonNullable(error.referenceLineNumber) ? `at line ${filePath}:${error.referenceLineNumber}` : ''}\n`
				}
				return output
			}),
			hasErrors: hasItemsAtLowestLevel(missingFiles),
		},
		configurationErrors: {
			errorOutput: printConfigurationErrors(configurationErrors),
			hasErrors: hasItemsAtLowestLevel(configurationErrors),
		},
	}
}

export function processWarnings({ relativeLinks }: ProjectErrors['rawWarnings']) {
	return {
		relativeLinks: {
			errorOutput: processGuideFileErrorMap(relativeLinks, (filePath, errors) => {
				let output = `File: ${filePath}\n`
				for (const error of errors) {
					output += ` - Relative Link: ${error.link} at line ${filePath}:${error.referenceLineNumber}\n`
				}
				return output
			}),
			hasErrors: hasItemsAtLowestLevel(relativeLinks),
		},
	}
}
